import React, { useEffect, useMemo, useState } from 'react';
import { Summary } from './past-meetings-detail.types.ts';
import '@/assets/styles/views/coach/past-meetings-detail.scss';
import LineDot from '@/assets/svg/line-dot.svg?react';
import clsx from 'clsx';
import { convertMeetingType, formatMilliseconds, isNullOrEmptyOrUndefined } from '@/utility';
import { useLocation, useNavigate } from 'react-router';
import { ReportModal, Modal, MeetReportForm, LoadingSpinner } from '@/components';
import StudentRatingModal from '@/components/student-rating-modal';
import { useQueryClient } from 'react-query';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import {
	GotItExpertInteractionFeedbackCreateRequest,
	GotItExpertInteractionFeedbackUpdateRequest,
	CoachExpertInteractionFeedbackCreateRequestConnectionStatusEnum as ConnectionStatusEnum,
	InteractionDetailResponseInteractionStatusEnum
} from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { toast } from 'react-toastify';
import { CustomPlaceholder } from '@/components/custom-placeholder/CustomPlaceholder.tsx';
import dayjs from 'dayjs';
import { useSizes } from '@/utility/responsive.ts';
import { NavigationPath } from '@/router/navigation-path.ts';
import CircleLink from '@/assets/svg/link-circle-blue.svg';
import { useFeatureFlag } from 'configcat-react';
import { BackupRoomStudentInvitationRequest } from '@dopingtech/live_expert_bff_api_ts';
import { INPUT_MAX_LENGTH } from '@/constants';

const MAX_CONTENT_LENGTH = INPUT_MAX_LENGTH.FEEDBACK;

const MeetingDetails: React.FC = () => {
	const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [isStudentRatingModalOpen, setStudentRatingModalOpen] = useState(false);
	const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
	const [activeSummary, setActiveSummary] = useState<Summary | null>(null);
	const [editingContent, setEditingContent] = useState('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { isMobile } = useSizes();

	const [isSendedBackupLink, setIsSendedBackupLink] = useState(false);
	const { value: isActiveFromOutsideMeetLink } = useFeatureFlag('isActiveFromOutsideMeetLink', false);
	const sendBackupRoomInvitationToStudentMutation = useMutationProvider((data: BackupRoomStudentInvitationRequest) =>
		BackupRoomControllerApi.sendBackupRoomInvitationToStudent({ studentId: data?.studentId })
	);

	const [status, setStatus] = useState<ConnectionStatusEnum>(ConnectionStatusEnum.Completed);
	const [summaries, setSummaries] = useState<Summary[]>([
		{
			id: 'commentToOperation',
			title: 'Operasyon Özeti',
			description: 'Öğrenciyle yaptığınız görüşmeye dair genel değerlendirmenizi, yaşadığınız bir sorun ya da iletmek istediğiniz ek konuları bu alana yazabilirsiniz.',
			placeholder: 'Öğrenci ile ilgili genel değerlendirmenizi yazın.',
			content: '',
			isEditing: false,
			icon: <i className="icons-checklist-minimalistic" />
		}
	]);
	const { state } = useLocation();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { FeedbackControllerApi, InteractionControllerApi, BackupRoomControllerApi } = useApi();

	const {
		data: interactionFeedback,
		isLoading: interactionFeedbackLoading,
		refetch: refetchInteractionFeedback
	} = useQueryProvider([QUERY_KEYS.findInteractionById, state?.interactionId], () => InteractionControllerApi.findInteractionById(state?.interactionId), {
		select: ({ data }) => data?.data
	});

	const isRefunded = useMemo(() => Boolean(interactionFeedback?.isManualRefund || interactionFeedback?.isSystemRefund), [interactionFeedback]);

	const saveFeedback = useMutationProvider(
		({ id, feedBack }: { id: number; feedBack: GotItExpertInteractionFeedbackUpdateRequest }) => FeedbackControllerApi.updateGotItExpertInteractionFeedback(id, feedBack),
		{
			onSuccess: () => {
				toast.success('Geri bildirim başarıyla kaydedildi.');
			},
			onError: () => {
				setIsLoading(false);
			}
		}
	);

	const onSendLinkToStudent = async (appointment: any) => {
		const entity: BackupRoomStudentInvitationRequest = {};

		entity.studentId = interactionFeedback?.studentId;

		const result = await sendBackupRoomInvitationToStudentMutation.mutateAsync(entity);

		if (result) {
			setIsSendedBackupLink(true);

			setTimeout(() => {
				setIsSendedBackupLink(false);
			}, 2000);
		}

		toast.success('Görüşme linki öğrenciye gönderildi!');
	};

	const createFeedback = useMutationProvider((body: GotItExpertInteractionFeedbackCreateRequest) => FeedbackControllerApi.createGotItExpertInteractionFeedback(body), {
		onSuccess: () => {
			toast.success('Geri bildirim başarıyla kaydedildi.');
		},
		onError: () => {
			setIsLoading(false);
		}
	});

	const converStatus = (interactionStatus: InteractionDetailResponseInteractionStatusEnum) => {
		switch (interactionStatus) {
			case InteractionDetailResponseInteractionStatusEnum.MeetingEnded:
			case InteractionDetailResponseInteractionStatusEnum.MeetingEndedSuccessfully:
				return ConnectionStatusEnum.Completed;
			case InteractionDetailResponseInteractionStatusEnum.MeetingFailed:
			case InteractionDetailResponseInteractionStatusEnum.MeetingFailedWithConnectionProblems:
				return ConnectionStatusEnum.Failed;
			case InteractionDetailResponseInteractionStatusEnum.MeetingEndedWithConnectionProblems:
				return ConnectionStatusEnum.CompletedWithMinorConnectionIssues;
			default:
				return ConnectionStatusEnum.Failed;
		}
	};

	useEffect(() => {
		if (interactionFeedback?.commentToOperation) {
			setSummaries(prev =>
				prev.map(summary => ({
					...summary,
					content: (interactionFeedback?.[summary?.id as keyof typeof interactionFeedback] as string) || summary.content || ''
				}))
			);
		}
		if (interactionFeedback?.interactionStatus) {
			setStatus(converStatus(interactionFeedback.interactionStatus));
		}
	}, [interactionFeedback]);

	const handleEdit = (id: string) => {
		setSummaries(prev =>
			prev.map(summary => ({
				...summary,
				isEditing: summary.id === id ? true : summary.isEditing
			}))
		);
	};

	const handleSave = async (id: string, content: string) => {
		if (content?.length > MAX_CONTENT_LENGTH) {
			toast.error(`Özet ${MAX_CONTENT_LENGTH} karakterden uzun olamaz.`);

			return;
		}

		const feedBack: GotItExpertInteractionFeedbackUpdateRequest = {};

		if (id === 'commentToOperation') {
			feedBack.comment = content;
		}

		if (interactionFeedback?.feedbackId) {
			await saveFeedback.mutateAsync({
				id: interactionFeedback?.feedbackId,
				feedBack: feedBack
			});
		} else {
			await createFeedback.mutateAsync({
				...feedBack,
				interactionId: state?.interactionId,
				connectionStatus: status
			});
		}

		setSummaries(prev =>
			prev.map(summary => ({
				...summary,
				isEditing: summary.id === id ? false : summary.isEditing
			}))
		);

		queryClient.refetchQueries([QUERY_KEYS.findInteractionById]);
	};

	const handleOpenComment = (summary: Summary) => {
		if (isMobile) {
			setActiveSummary(summary);
			setEditingContent(summary.content);
			setIsCommentModalOpen(true);
		} else {
			handleEdit(summary.id);
		}
	};

	const handleModalSave = async () => {
		if (!activeSummary) return;
		setIsLoading(true);

		await handleSave(activeSummary.id, editingContent);

		setIsLoading(false);
		setIsCommentModalOpen(false);
		setActiveSummary(null);
		setEditingContent('');

		await queryClient.refetchQueries([QUERY_KEYS.findInteractionById]);
	};

	const onEditing = (id: string, content: string) => {
		setSummaries(prev =>
			prev.map(summary => ({
				...summary,
				content: summary.id === id ? content : summary.content
			}))
		);
	};

	const onChangeStatus = async () => {
		const feedBack: GotItExpertInteractionFeedbackUpdateRequest = {
			connectionStatus: status !== ConnectionStatusEnum.Failed ? ConnectionStatusEnum.Failed : ConnectionStatusEnum.Completed
		};
		if (interactionFeedback?.feedbackId) {
			setStatus(prevState => {
				return prevState !== ConnectionStatusEnum.Failed ? ConnectionStatusEnum.Failed : ConnectionStatusEnum.Completed;
			});

			await saveFeedback.mutateAsync({
				id: interactionFeedback?.feedbackId,
				feedBack: feedBack
			});
		} else {
			await createFeedback.mutateAsync({
				...feedBack,
				interactionId: state?.interactionId
			});
		}

		await queryClient.refetchQueries([QUERY_KEYS.findInteractionById, state?.interactionId]);

		await refetchInteractionFeedback();

		setIsStatusDropdownOpen(false);
	};

	const onBack = () => {
		navigate(NavigationPath.gotItPastMeetings);
	};

	const onNavigateStudentCard = () => {
		navigate(NavigationPath.gotItStudentDetails, {
			state: {
				studentId: interactionFeedback?.studentId,
				openMeetingsTab: true
			}
		});
	};

	const isConnectionIssues = useMemo(() => {
		if (
			interactionFeedback?.interactionStatus === InteractionDetailResponseInteractionStatusEnum.MeetingFailedWithConnectionProblems ||
			interactionFeedback?.interactionStatus === InteractionDetailResponseInteractionStatusEnum.MeetingEndedWithConnectionProblems
		) {
			return true;
		} else {
			return false;
		}
	}, [interactionFeedback]);

	return (
		<div className="meeting-details">
			<header className="meeting-details__header">
				<a
					href="#"
					className="meeting-details__header-back"
					onClick={onBack}>
					<i className="icons-arrow-left" />
				</a>
				<div className="meeting-details__header-profile-image">
					<CustomPlaceholder
						ready={!interactionFeedbackLoading}
						type="round">
						<img
							src={interactionFeedback?.studentAvatarUrl}
							alt={interactionFeedback?.studentName}
						/>
					</CustomPlaceholder>
				</div>
				<div className="meeting-details__header-info">
					<CustomPlaceholder
						ready={!interactionFeedbackLoading}
						type="rect"
						rows={2}
						width={80}
						height={30}>
						<div className="meeting-details__header-info-name">{`${interactionFeedback?.studentName} ${interactionFeedback?.studentSurname?.[0]}.`}</div>
						<div className="meeting-details__header-info-grade">{interactionFeedback?.studentGradeName}</div>
					</CustomPlaceholder>
				</div>
				<div className="meeting-details__header-actions">
					<button
						className="button-profile-card"
						onClick={onNavigateStudentCard}>
						<i className="icons-user" />
						{!isMobile && 'Profil Kartını Görüntüle'}
					</button>
					{status !== ConnectionStatusEnum.Failed && (
						<button
							className="meeting-details__header-actions-report"
							onClick={() => setIsReportModalOpen(true)}>
							<i className="icons-danger-circle" />
							{!isMobile && 'Raporla'}
						</button>
					)}
				</div>
			</header>
			<div className="meeting-details__status">
				<CustomPlaceholder
					ready={!interactionFeedbackLoading}
					type="rect"
					height={26}
					className="meeting-details__status-left-side">
					<div className="meeting-details__status-left-side">
						<div className="meeting-details__status-dropdown">
							<button
								className={`meeting-details__status-dropdown-button ${status !== ConnectionStatusEnum.Failed ? '' : 'meeting-details__status-dropdown-button--failed'}`}
								onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}>
								{status !== ConnectionStatusEnum.Failed ? 'Gerçekleşti' : 'Gerçekleşmedi'}
								{isStatusDropdownOpen ? <i className="icons-alt-arrow-up" /> : <i className="icons-alt-arrow-down" />}
							</button>
							{isStatusDropdownOpen && (
								<div className="meeting-details__status-dropdown-content">
									<button
										onClick={onChangeStatus}
										className={status === ConnectionStatusEnum.Failed ? '' : 'failed'}>
										{status === ConnectionStatusEnum.Failed ? 'Gerçekleşti' : 'Gerçekleşmedi'}
									</button>
								</div>
							)}
						</div>
						{isConnectionIssues && (
							<>
								<LineDot
									className="meeting-details__status-separator"
									color={status !== ConnectionStatusEnum.Failed ? '#1ABC9C' : 'var(--danger-text-strong)'}
								/>

								<span
									className={clsx(
										'meeting-details__status-issue',
										interactionFeedback?.interactionStatus === InteractionDetailResponseInteractionStatusEnum?.MeetingFailedWithConnectionProblems ||
											interactionFeedback?.interactionStatus === InteractionDetailResponseInteractionStatusEnum?.MeetingEndedWithConnectionProblems
											? 'failed'
											: ''
									)}>
									Bağlantı Sorunu Yaşandı
								</span>
							</>
						)}
					</div>
				</CustomPlaceholder>
				<CustomPlaceholder
					ready={!interactionFeedbackLoading}
					type="rect"
					height={26}
					width={135}
					className="meeting-details__status-rating">
					<div className="meeting-details__status-rating">
						{isActiveFromOutsideMeetLink && interactionFeedback?.isActiveMeeting && (
							<div
								className={clsx('connect-alternative-link', { disabled: sendBackupRoomInvitationToStudentMutation?.isLoading || isSendedBackupLink })}
								onClick={e => {
									onSendLinkToStudent();
									e.stopPropagation();
								}}>
								<img src={CircleLink} />
								{isSendedBackupLink ? 'Link gönderildi' : 'Öğrenciye Link Gönder'}
							</div>
						)}
						<div className="meeting-details__status-rating-top">
							{isRefunded && <span className="meeting-details__status-rating-refund">Kredi İade Edildi</span>}
							{status !== ConnectionStatusEnum.Failed && interactionFeedback?.commentToExpert && (
								<span onClick={() => interactionFeedback?.rating && setStudentRatingModalOpen(true)}>
									<i className="icons-chat-line" />
									{interactionFeedback?.commentToExpert ? 1 : 0}
								</span>
							)}
							<div
								className={clsx('meeting-details__status-rating-stars', {
									clickable: status !== ConnectionStatusEnum.Failed && interactionFeedback?.rating
								})}
								onClick={() => status !== ConnectionStatusEnum.Failed && interactionFeedback?.rating && setStudentRatingModalOpen(true)}>
								{[1, 2, 3, 4, 5].map(star => (
									<i
										key={star}
										className={clsx(
											'icons-star',
											star <= (interactionFeedback?.rating || 0)
												? clsx('filled', {
														red: (interactionFeedback?.rating || 0) < 3,
														orange: interactionFeedback?.rating === 3
													})
												: ''
										)}
									/>
								))}
							</div>
						</div>
						{status !== ConnectionStatusEnum.Failed ? (
							<span className="meeting-details__status-rating-label">{interactionFeedback?.rating ? 'Öğrenci Puanı' : 'Henüz Puanlamadı'}</span>
						) : (
							<span className="meeting-details__status-rating-label red">Sistem Puanı</span>
						)}
					</div>
				</CustomPlaceholder>
			</div>

			<div className="meeting-details__info">
				<div className="meeting-details__info-item">
					<i className="icons-calendar" />
					<div className="meeting-details__info-item-label">Görüşme Tarihi</div>
					<div className="meeting-details__info-item-value">{dayjs(interactionFeedback?.interactionStartDate).format('D MMMM, HH:mm')}</div>
				</div>
				<div className="meeting-details__info-vertical-seperator" />
				<div className="meeting-details__info-item">
					<i className="icons-alarm" />
					<div className="meeting-details__info-item-label">Görüşme Süresi</div>
					<div className="meeting-details__info-item-value">{formatMilliseconds(interactionFeedback?.meetingDuration)}</div>
				</div>
				<div className="meeting-details__info-item">
					<i className="icons-extend-call" />
					<div className="meeting-details__info-item-label">Katılım Süreniz</div>
					<div className="meeting-details__info-item-value">{formatMilliseconds(interactionFeedback?.expertMeetingDuration || '0')}</div>
				</div>
				<div className="meeting-details__info-vertical-seperator" />
				<div className="meeting-details__info-item">
					<i className="icons-videocamera-record" />
					<div className="meeting-details__info-item-label">Görüşme Türü</div>
					<div className="meeting-details__info-item-value">{convertMeetingType(interactionFeedback?.meetingType, 'APPOINTMENT')}</div>
				</div>
				<div className="meeting-details__info-vertical-seperator" />
				<div className="meeting-details__info-item">
					<i className="icons-document" />
					<div className="meeting-details__info-item-label">{interactionFeedback?.parentMeetingSubjectName}</div>
					<div className="meeting-details__info-item-value">{interactionFeedback?.meetingSubjectName}</div>
				</div>
			</div>

			{status !== ConnectionStatusEnum.Failed &&
				summaries?.map(summary => (
					<CustomPlaceholder
						ready={!interactionFeedbackLoading}
						type="rect"
						key={summary.id}
						height={66}
						className="meeting-details__summary">
						<div
							key={summary.id}
							className="meeting-details__summary">
							<div className="meeting-details__summary-header">
								<div className="meeting-details__summary-header-icon">{summary.icon}</div>
								<div className="meeting-details__summary-header-content">
									<div className="meeting-details__summary-header-title">{summary.title}</div>
									{isMobile ? (
										isNullOrEmptyOrUndefined(summary.content) && <div className="meeting-details__summary-header-description">{summary.description}</div>
									) : (
										<div className="meeting-details__summary-header-description">{summary.description}</div>
									)}
								</div>
								{!isMobile ? (
									summary.isEditing ? null : isNullOrEmptyOrUndefined(summary.content) ? (
										<div className="meeting-details__summary-header-actions">
											<button
												className="meeting-details__summary-header-actions-button"
												onClick={() => handleOpenComment(summary)}>
												<i className="icons-dialog" />
												Yorum Gir
											</button>
										</div>
									) : (
										<div className="meeting-details__summary-header-actions">
											<button
												className="meeting-details__summary-header-actions-edit"
												onClick={() => handleOpenComment(summary)}>
												<i className="icons-pen" />
											</button>
										</div>
									)
								) : (
									!summary.isEditing &&
									!isNullOrEmptyOrUndefined(summary.content) && (
										<div className="meeting-details__summary-header-actions">
											<button
												className="meeting-details__summary-header-actions-edit"
												onClick={() => handleOpenComment(summary)}>
												<i className="icons-pen" />
											</button>
										</div>
									)
								)}
							</div>

							{isMobile && !summary.isEditing && isNullOrEmptyOrUndefined(summary.content) && (
								<div className="meeting-details__summary-header-actions mobile">
									<button
										className="meeting-details__summary-header-actions-button"
										onClick={() => handleOpenComment(summary)}>
										<i className="icons-dialog" />
										Yorum Gir
									</button>
								</div>
							)}

							{summary.isEditing ? (
								<div className="meeting-details__summary-content">
									<textarea
										className="meeting-details__summary-content-input"
										placeholder={summary.placeholder}
										value={summary.content}
										maxLength={MAX_CONTENT_LENGTH}
										onChange={e => onEditing(summary.id, e.target.value)}
									/>
									<div className="meeting-details__summary-content-actions">
										<button
											className="save"
											onClick={() => handleSave(summary.id, summary.content)}>
											<i className="icons-diskette" />
											Kaydet
										</button>
									</div>
								</div>
							) : (
								summary.content && (
									<div className="meeting-details__summary-content">
										<div className="meeting-details__summary-content-text">{summary.content}</div>
									</div>
								)
							)}
							{summary?.isEditing && (
								<div
									className={clsx('meeting-details__summary-content-length-view', {
										exceeded: summary?.content?.length > MAX_CONTENT_LENGTH
									})}>
									{summary?.content?.length || 0}/{MAX_CONTENT_LENGTH}
								</div>
							)}
						</div>
					</CustomPlaceholder>
				))}

			<MeetReportForm
				defaultOpen={status === ConnectionStatusEnum.Failed}
				interactionId={state?.interactionId}
			/>

			<ReportModal
				isOpen={isReportModalOpen}
				onClose={() => {
					setIsReportModalOpen(false);
				}}
				studentId={interactionFeedback?.studentId}
				interactionId={state?.interactionId}
			/>
			{interactionFeedback && (
				<StudentRatingModal
					isOpen={isStudentRatingModalOpen}
					onClose={() => {
						setStudentRatingModalOpen(false);
					}}
					interactionFeedback={interactionFeedback}
				/>
			)}

			{isMobile && (
				<Modal
					isOpen={isCommentModalOpen}
					onClose={() => {
						setIsCommentModalOpen(false);
						setActiveSummary(null);
						setEditingContent('');
					}}
					variant="bottom"
					size="xl"
					showCloseButton={true}
					contentClassName="comment-bottom-sheet">
					<div className="comment-bottom-sheet__content">
						<div className="comment-bottom-sheet__header">
							<div className="comment-bottom-sheet__title">
								{activeSummary?.icon}
								{activeSummary?.title}
							</div>
							<div className="comment-bottom-sheet__description">{activeSummary?.description}</div>
						</div>

						<textarea
							className="comment-bottom-sheet__textarea"
							placeholder={activeSummary?.placeholder}
							value={editingContent}
							maxLength={MAX_CONTENT_LENGTH}
							onChange={e => setEditingContent(e.target.value)}></textarea>

						<div
							className={clsx('comment-bottom-sheet__textarea-length-view', {
								exceeded: editingContent?.length > MAX_CONTENT_LENGTH
							})}>
							{editingContent?.length || 0}/{MAX_CONTENT_LENGTH}
						</div>

						<div className="comment-bottom-sheet__actions">
							<button
								className={`comment-bottom-sheet__save-button ${!editingContent.trim() ? 'disabled' : ''}`}
								onClick={handleModalSave}
								disabled={!editingContent.trim()}>
								Kaydet
							</button>
						</div>
					</div>
				</Modal>
			)}

			{isLoading && <LoadingSpinner loadingText="Kaydediliyor" />}
		</div>
	);
};

export default MeetingDetails;
